import { v4 as uuidv4 } from 'uuid';
import { getSignUrl, uploadFile } from 'api/upload';
import {
  findRichMessageTemplate,
  RICH_MESSAGE_BASE_WIDTH,
  RICH_MESSAGE_CUSTOM_MAX_HEIGHT,
  RICH_MESSAGE_CUSTOM_MIN_HEIGHT,
} from 'constants/richMessageTemplates';
import {
  ERichMenuAreaActionType,
  ERichMessageTemplateCategory,
  TRichMessageAreaAction,
  TRichMessageAreaPayload,
  TRichMessageImagemapPayload,
  TRichMessageTemplate,
} from 'types';

/** LINE imagemap fetches {baseUrl}/240 … /1040 — all must exist. */
export const LINE_IMAGEMAP_WIDTHS = [240, 300, 460, 700, 1040] as const;

export async function uploadImagemapImage(file: File): Promise<{
  imageUrl: string;
  imagemapBaseUrl: string;
}> {
  const baseId = uuidv4();
  const contentType = file.type || 'image/png';

  const results = await Promise.all(
    LINE_IMAGEMAP_WIDTHS.map(async (width) => {
      const signed = await getSignUrl({
        filename: `${baseId}/${width}`,
        contentType,
        size: file.size,
      });
      await uploadFile(signed.preSignedUrl, file);
      return signed;
    }),
  );

  const fullSize = results.find((item) => item.fileName.endsWith('/1040'));
  if (!fullSize?.uploadedUrl) {
    throw new Error('Failed to upload imagemap image');
  }

  const imagemapBaseUrl = fullSize.uploadedUrl.replace(/\/1040$/, '');
  return {
    imageUrl: fullSize.uploadedUrl,
    imagemapBaseUrl,
  };
}

export function resolveImagemapBaseUrl(payload: {
  imagemapBaseUrl?: string;
  imageUrl?: string;
}): string | undefined {
  const explicit = payload.imagemapBaseUrl?.trim();
  if (explicit) return explicit.replace(/\/$/, '');

  const imageUrl = payload.imageUrl?.trim();
  if (!imageUrl) return undefined;

  if (/\/1040$/i.test(imageUrl)) {
    return imageUrl.replace(/\/1040$/i, '');
  }

  return undefined;
}

export function isLegacyImagemapImageUrl(imageUrl: string): boolean {
  return /\.(jpe?g|png)$/i.test(imageUrl);
}

export function createDefaultAreaActions(
  template: TRichMessageTemplate,
): TRichMessageAreaAction[] {
  return template.areaKeys.map(() => ({ type: ERichMenuAreaActionType.None }));
}

export function buildAreasPayload(
  template: TRichMessageTemplate,
  actions: TRichMessageAreaAction[],
  customHeight?: number,
): TRichMessageAreaPayload[] {
  const height =
    template.category === ERichMessageTemplateCategory.Custom && customHeight
      ? clampCustomHeight(customHeight)
      : template.size.height;

  return template.bounds.map((bounds, index) => {
    const scaledBounds =
      template.category === ERichMessageTemplateCategory.Custom && customHeight
        ? { ...bounds, height }
        : bounds;

    return {
      key: template.areaKeys[index] ?? String(index),
      bounds: scaledBounds,
      action: actions[index] ?? { type: ERichMenuAreaActionType.None },
    };
  });
}

export function buildRichMessageImagemapPayload(params: {
  name: string;
  altText: string;
  templateId: string;
  templates: TRichMessageTemplate[];
  imageUrl: string;
  imagemapBaseUrl: string;
  areas: TRichMessageAreaAction[];
  customHeight?: number;
}): TRichMessageImagemapPayload {
  const template = findRichMessageTemplate(params.templates, params.templateId);
  if (!template) {
    throw new Error('Invalid rich message template');
  }

  const height =
    template.category === ERichMessageTemplateCategory.Custom
      ? clampCustomHeight(params.customHeight ?? template.size.height)
      : template.size.height;

  return {
    altText: params.altText.trim() || params.name.trim(),
    templateId: params.templateId,
    imageUrl: params.imageUrl,
    imagemapBaseUrl: params.imagemapBaseUrl,
    baseSize: { width: RICH_MESSAGE_BASE_WIDTH, height },
    areas: buildAreasPayload(template, params.areas, height),
  };
}

export function parseRichMessagePayload(
  payload: unknown,
  templates?: TRichMessageTemplate[],
): {
  templateId: string;
  altText: string;
  imageUrl: string;
  imagemapBaseUrl?: string;
  areas: TRichMessageAreaAction[];
  customHeight?: number;
} | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as TRichMessageImagemapPayload & {
    actionUrl?: string;
    actionLabel?: string;
    imagemapBaseUrl?: string;
  };
  if (!record.imageUrl) return null;

  const templateId = record.templateId ?? 'SQ_1040_FULL';
  const template = findRichMessageTemplate(templates, templateId);
  const defaultActions = template ? createDefaultAreaActions(template) : [];
  const imagemapBaseUrl = resolveImagemapBaseUrl(record);

  if (record.areas && record.areas.length > 0) {
    const areas = record.areas.map((area) => ({
      type:
        (area.action?.type as TRichMessageAreaAction['type']) ??
        ERichMenuAreaActionType.None,
      label: area.action?.label,
      uri: area.action?.uri,
      text: area.action?.text,
    }));
    return {
      templateId,
      altText: record.altText ?? '',
      imageUrl: record.imageUrl,
      imagemapBaseUrl,
      areas,
      customHeight: record.baseSize?.height,
    };
  }

  const legacyUri =
    typeof record.actionUrl === 'string' ? record.actionUrl.trim() : '';
  const areas: TRichMessageAreaAction[] = defaultActions.map(
    (action, index) => {
      if (index !== 0) return action;
      if (!legacyUri) return { type: ERichMenuAreaActionType.None };
      return {
        type: ERichMenuAreaActionType.Uri,
        uri: legacyUri,
        label:
          typeof record.actionLabel === 'string'
            ? record.actionLabel
            : undefined,
      };
    },
  );

  return {
    templateId,
    altText: record.altText ?? '',
    imageUrl: record.imageUrl,
    imagemapBaseUrl,
    areas,
    customHeight: record.baseSize?.height,
  };
}

/** 現行形式へ復元できない素材から、修復editorへ引き継げる値だけを取り出す。 */
export function readRichMessageRepairHints(asset: {
  name: string;
  payload: unknown;
  previewImageUrl?: string | null;
}): {
  name: string;
  altText?: string;
  imageUrl?: string;
  imagemapBaseUrl?: string;
} {
  const record = (
    asset.payload && typeof asset.payload === 'object' ? asset.payload : {}
  ) as { altText?: unknown; imageUrl?: unknown };
  const imageUrl = [asset.previewImageUrl, record.imageUrl].find(
    (value): value is string => typeof value === 'string' && !!value.trim(),
  );

  return {
    name: asset.name,
    altText: typeof record.altText === 'string' ? record.altText : undefined,
    imageUrl,
    imagemapBaseUrl: imageUrl ? resolveImagemapBaseUrl({ imageUrl }) : undefined,
  };
}

export function clampCustomHeight(height: number): number {
  return Math.min(
    RICH_MESSAGE_CUSTOM_MAX_HEIGHT,
    Math.max(RICH_MESSAGE_CUSTOM_MIN_HEIGHT, Math.round(height)),
  );
}

export async function readImageDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}
